import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, viewChild, WritableSignal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize, map, of, Subject, switchMap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { Toast } from '@/shared/components/toast/toast';
import { TooltipModule } from 'primeng/tooltip';
import { CardContent } from '@/shared/components/card-content/card-content';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';
import { SelecionaContaContabil } from '@/shared/components/seleciona-conta-contabil/seleciona-conta-contabil';
import { ContaConfigService } from '@/core/api/sicom-api/financeiro/arrecadacao/conta-config/conta-config.service';
import { ContaConfigRequest, ContaConfigResponse } from '@/core/api/sicom-api/financeiro/arrecadacao/conta-config/conta-config.model';
import { ContaContabilService } from '@/core/api/sicom-api/financeiro/arrecadacao/conta-contabil/conta-contabil.service';
import { ContaContabilResponse } from '@/core/api/sicom-api/financeiro/arrecadacao/conta-contabil/conta-contabil.model';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-contabilidade',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, Toast, TooltipModule, CardContent, FiltrosBar, FiltroCampo, SelectEstado, SelecionaContaContabil, CanDirective],
    providers: [MessageService],
    templateUrl: './contabilidade.html',
    styleUrl: './contabilidade.scss'
})
/** Configuração de contabilidade (contas e parâmetros contábeis da arrecadação). */
export class Contabilidade implements OnInit {
    private readonly contaConfigService = inject(ContaConfigService);
    private readonly contaContabilService = inject(ContaContabilService);
    private readonly messageService = inject(MessageService);
    private readonly pessoaSessionService = inject(PessoaService);
    private readonly permissionService = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly seletorConta = viewChild.required(SelecionaContaContabil);
    private contaSetCallback: ((id: string) => void) | null = null;

    protected readonly rolePermissaoEditar = PERMISSOES.configuracoes.contabilidade.editar;
    /** Fila da UF a carregar; o switchMap cancela a busca anterior ao trocar de UF rapidamente. */
    private readonly carregarUf$ = new Subject<string>();
    private readonly configVazia: ContaConfigResponse = {};

    protected readonly sgUf = signal<string>('');

    /** Regional não escolhe UF: o backend impõe a dele, e o select reflete isso em vez de mentir. */
    protected readonly ufTravada = computed(() => !this.permissionService.isCfm);

    /**
     * Autosseleção do primeiro estado só para quem escolhe. Sem UF resolvida (escopo indefinido) o
     * select travado pararia no 'AC' e sugeriria que o operador está vendo dado do Acre — ele não vê
     * nenhum: o backend falha fechado. Melhor vazio e desabilitado.
     */
    protected readonly autoSelecionarUf = computed(() => !this.ufTravada() || !!this.ufOperador());

    protected readonly editando = signal(false);
    protected readonly carregando = signal(false);
    protected readonly salvando = signal(false);

    // Plano de contas — usado para exibir "código - descrição" nos campos
    protected readonly contasContabil = signal<ContaContabilResponse[]>([]);

    // ── Anuidades
    protected readonly anuidadePf = signal<string | null>(null);
    protected readonly anuidadePj = signal<string | null>(null);
    protected readonly anuidadeAnteriorPf = signal<string | null>(null);
    protected readonly anuidadeAnteriorPj = signal<string | null>(null);

    // ── Multas e Juros
    protected readonly multaPf = signal<string | null>(null);
    protected readonly multaPj = signal<string | null>(null);
    protected readonly jurosPf = signal<string | null>(null);
    protected readonly jurosPj = signal<string | null>(null);

    // ── Dívida Ativa Administrativa
    protected readonly vrAdmPf = signal<string | null>(null);
    protected readonly vrAdmPj = signal<string | null>(null);
    protected readonly multaDaPf = signal<string | null>(null);
    protected readonly multaDaPj = signal<string | null>(null);
    protected readonly jurosDaPf = signal<string | null>(null);
    protected readonly jurosDaPj = signal<string | null>(null);

    // ── Dívida Ativa Executiva
    protected readonly vrExePf = signal<string | null>(null);
    protected readonly vrExePj = signal<string | null>(null);
    protected readonly multaExePf = signal<string | null>(null);
    protected readonly multaExePj = signal<string | null>(null);
    protected readonly jurosExePf = signal<string | null>(null);
    protected readonly jurosExePj = signal<string | null>(null);

    // ── Cota Parte
    protected readonly cotaParte = signal<string | null>(null);

    // ── Entradas
    protected readonly entradaPix = signal<string | null>(null);
    protected readonly entradaCredito = signal<string | null>(null);
    protected readonly entradaDebito = signal<string | null>(null);
    protected readonly entradaBoleto = signal<string | null>(null);

    // ── Crédito a Restituir
    protected readonly duplicadoPf = signal<string | null>(null);
    protected readonly duplicadoPj = signal<string | null>(null);

    get camposDesabilitados(): boolean {
        return !this.editando() || this.carregando() || this.salvando();
    }

    constructor() {
        // Cada UF emitida cancela a busca anterior (switchMap), evitando que uma resposta atrasada sobrescreva outra.
        this.carregarUf$
            .pipe(
                switchMap((uf) => {
                    if (!uf) {
                        return of({ response: this.configVazia, uf: '' });
                    }
                    this.carregando.set(true);
                    return this.contaConfigService.buscar(uf).pipe(
                        map((response) => ({ response, uf })),
                        catchError(() => {
                            return EMPTY;
                        }),
                        finalize(() => this.carregando.set(false))
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(({ response, uf }) => this.popularFormulario(response, uf));
    }

    ngOnInit(): void {
        this.carregarContasContabil();
        this.inicializarUf();
    }

    /**
     * Pré-seleciona a UF do usuário logado e carrega a configuração dela. Se a UF for BR/ausente,
     * deixa o combo (autoSelectFirst) escolher o primeiro estado da lista e disparar o carregamento.
     */
    private inicializarUf(): void {
        // Regional: a UF vem do escopo do operador (a mesma que o backend impõe); só no CFM a sessão decide.
        const ufUsuario = this.ufOperador() || (this.pessoaSessionService.getPessoaReference()?.uf ?? '').trim().toUpperCase();
        if (/^[A-Z]{2}$/.test(ufUsuario) && ufUsuario !== 'BR') {
            this.sgUf.set(ufUsuario);
            this.carregar(ufUsuario);
        }
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.permissionService.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    protected getDescricaoConta(id: number | string | null): string {
        if (id == null) return '';
        const conta = this.contasContabil().find((c) => String(c.id) === String(id));
        return conta ? conta.descricao : '';
    }

    protected getFormatConta(id: number | string | null): string {
        if (id == null) return 'Não configurado';
        const desc = this.getDescricaoConta(id);
        return desc ? `${id} - ${desc}` : String(id);
    }

    /** Ao trocar a UF no combo, carrega a configuração contábil daquela UF. */
    protected onUfChange(uf: string): void {
        this.sgUf.set(uf);
        this.carregar(uf);
    }

    protected editar(): void {
        this.editando.set(true);
    }

    protected cancelar(): void {
        this.editando.set(false);
        this.carregar(this.sgUf());
    }

    protected salvar(): void {
        if (!this.sgUf()) {
            this.messageService.add({ severity: 'warn', summary: 'Atenção', detail: 'Selecione uma UF antes de salvar.' });
            return;
        }
        this.salvando.set(true);
        this.contaConfigService
            .salvar(this.buildRequest())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.popularFormulario(response, this.sgUf());
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Configuração de contabilidade salva com sucesso.' });
                    this.editando.set(false);
                },
                error: () => this.salvando.set(false),
                complete: () => this.salvando.set(false)
            });
    }

    // ── Seleção de conta (modal compartilhado) ─────────────────────
    protected abrirSelecionarConta(sinal: WritableSignal<string | null>): void {
        this.contaSetCallback = (id: string) => sinal.set(id);
        this.seletorConta().abrir();
    }

    protected limparConta(sinal: WritableSignal<string | null>): void {
        sinal.set(null);
    }

    protected onContaSelecionada(conta: ContaContabilResponse): void {
        this.contaSetCallback?.(conta.id);
        this.contaSetCallback = null;
    }

    private carregarContasContabil(): void {
        this.contaContabilService
            .listar()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (contas) => this.contasContabil.set(contas),
                error: () => this.contasContabil.set([])
            });
    }

    /** Solicita o carregamento da UF; o pipeline do constructor faz a busca (cancelando a anterior). */
    private carregar(uf: string): void {
        this.carregarUf$.next(uf);
    }

    /** Popula os campos com a resposta; mantém a UF selecionada mesmo quando ela ainda não tem registro. */
    private popularFormulario(response: ContaConfigResponse, uf: string): void {
        this.sgUf.set(response.sg_uf ?? uf);
        this.anuidadePf.set(response.id_anuidade_pf ?? null);
        this.anuidadePj.set(response.id_anuidade_pj ?? null);
        this.anuidadeAnteriorPf.set(response.id_anuidade_anterior_pf ?? null);
        this.anuidadeAnteriorPj.set(response.id_anuidade_anterior_pj ?? null);
        this.multaPf.set(response.id_multa_pf ?? null);
        this.multaPj.set(response.id_multa_pj ?? null);
        this.jurosPf.set(response.id_juros_pf ?? null);
        this.jurosPj.set(response.id_juros_pj ?? null);
        this.vrAdmPf.set(response.id_vr_adm_pf ?? null);
        this.vrAdmPj.set(response.id_vr_adm_pj ?? null);
        this.multaDaPf.set(response.id_multa_da_pf ?? null);
        this.multaDaPj.set(response.id_multa_da_pj ?? null);
        this.jurosDaPf.set(response.id_juros_da_pf ?? null);
        this.jurosDaPj.set(response.id_juros_da_pj ?? null);
        this.vrExePf.set(response.id_vr_exe_pf ?? null);
        this.vrExePj.set(response.id_vr_exe_pj ?? null);
        this.multaExePf.set(response.id_multa_exe_pf ?? null);
        this.multaExePj.set(response.id_multa_exe_pj ?? null);
        this.jurosExePf.set(response.id_juros_exe_pf ?? null);
        this.jurosExePj.set(response.id_juros_exe_pj ?? null);
        this.cotaParte.set(response.id_cota_parte ?? null);
        this.entradaPix.set(response.id_entrada_pix ?? null);
        this.entradaCredito.set(response.id_entrada_credito ?? null);
        this.entradaDebito.set(response.id_entrada_debito ?? null);
        this.entradaBoleto.set(response.id_entrada_boleto ?? null);
        this.duplicadoPf.set(response.id_duplicado_pf ?? null);
        this.duplicadoPj.set(response.id_duplicado_pj ?? null);
    }

    private buildRequest(): ContaConfigRequest {
        return {
            sg_uf: this.sgUf() || undefined,
            id_anuidade_pf: this.anuidadePf() ?? undefined,
            id_anuidade_pj: this.anuidadePj() ?? undefined,
            id_anuidade_anterior_pf: this.anuidadeAnteriorPf() ?? undefined,
            id_anuidade_anterior_pj: this.anuidadeAnteriorPj() ?? undefined,
            id_multa_pf: this.multaPf() ?? undefined,
            id_juros_pf: this.jurosPf() ?? undefined,
            id_multa_pj: this.multaPj() ?? undefined,
            id_juros_pj: this.jurosPj() ?? undefined,
            id_vr_adm_pf: this.vrAdmPf() ?? undefined,
            id_vr_adm_pj: this.vrAdmPj() ?? undefined,
            id_multa_da_pf: this.multaDaPf() ?? undefined,
            id_juros_da_pf: this.jurosDaPf() ?? undefined,
            id_multa_da_pj: this.multaDaPj() ?? undefined,
            id_juros_da_pj: this.jurosDaPj() ?? undefined,
            id_vr_exe_pf: this.vrExePf() ?? undefined,
            id_vr_exe_pj: this.vrExePj() ?? undefined,
            id_multa_exe_pf: this.multaExePf() ?? undefined,
            id_multa_exe_pj: this.multaExePj() ?? undefined,
            id_juros_exe_pf: this.jurosExePf() ?? undefined,
            id_juros_exe_pj: this.jurosExePj() ?? undefined,
            id_cota_parte: this.cotaParte() ?? undefined,
            id_entrada_pix: this.entradaPix() ?? undefined,
            id_entrada_credito: this.entradaCredito() ?? undefined,
            id_entrada_debito: this.entradaDebito() ?? undefined,
            id_entrada_boleto: this.entradaBoleto() ?? undefined,
            id_duplicado_pf: this.duplicadoPf() ?? undefined,
            id_duplicado_pj: this.duplicadoPj() ?? undefined
        };
    }
}
